import React, { useState } from 'react';
import { useCustomer } from '../customer/hooks/useCustomer';

interface CustomerSearchProps {
  onCustomerSelect: (customer: any) => void;
  selectedCustomer?: any;
}

export const CustomerSearch: React.FC<CustomerSearchProps> = ({ onCustomerSelect, selectedCustomer }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    phone: '',
    email: '',
    address: ''
  });

  const { customers, addCustomer } = useCustomer();

  const filteredCustomers = customers?.filter(customer =>
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.phone.includes(searchTerm) ||
    customer.email.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const handleNewCustomer = async () => {
    try {
      const customer = await addCustomer.mutateAsync(newCustomer);
      onCustomerSelect(customer);
      setShowNewCustomerForm(false);
      setNewCustomer({ name: '', phone: '', email: '', address: '' });
    } catch (error) {
      console.error('Failed to add customer:', error);
    }
  };

  return (
    <div className="bg-white rounded-lg border p-6 space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Customer Selection</h2>
        {selectedCustomer && (
          <div className="text-sm text-green-600 flex items-center">
            <i className="ri-check-line mr-1"></i>
            Customer Selected
          </div>
        )}
      </div>

      {selectedCustomer ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="font-semibold text-green-800">{selectedCustomer.name}</h3>
              <p className="text-sm text-green-600">{selectedCustomer.phone}</p>
              <p className="text-sm text-green-600">{selectedCustomer.email}</p>
            </div>
            <button
              onClick={() => onCustomerSelect(null)}
              className="text-green-600 hover:text-green-800"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            <div className="flex space-x-2">
              <input
                type="text"
                placeholder="Search by name, phone, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                onClick={() => setShowNewCustomerForm(true)}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 whitespace-nowrap"
              >
                <i className="ri-add-line mr-1"></i>
                New Customer
              </button>
            </div>

            {searchTerm && (
              <div className="max-h-60 overflow-y-auto border rounded-md">
                {filteredCustomers.length > 0 ? (
                  filteredCustomers.map((customer) => (
                    <div
                      key={customer.id}
                      onClick={() => onCustomerSelect(customer)}
                      className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium">{customer.name}</h4>
                          <p className="text-sm text-gray-600">{customer.phone}</p>
                          <p className="text-sm text-gray-600">{customer.email}</p>
                        </div>
                        <i className="ri-arrow-right-line text-gray-400"></i>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-4 text-center text-gray-500">
                    No customers found. Create a new customer?
                  </div>
                )}
              </div>
            )}
          </div>

          {showNewCustomerForm && (
            <div className="border rounded-lg p-4 bg-gray-50">
              <h3 className="font-semibold mb-3">Add New Customer</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="Customer Name *"
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer({...newCustomer, name: e.target.value})}
                  className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <input
                  type="tel"
                  placeholder="Phone Number *"
                  value={newCustomer.phone}
                  onChange={(e) => setNewCustomer({...newCustomer, phone: e.target.value})}
                  className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <input
                  type="email"
                  placeholder="Email Address"
                  value={newCustomer.email}
                  onChange={(e) => setNewCustomer({...newCustomer, email: e.target.value})}
                  className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <input
                  type="text"
                  placeholder="Address"
                  value={newCustomer.address}
                  onChange={(e) => setNewCustomer({...newCustomer, address: e.target.value})}
                  className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="flex space-x-2 mt-3">
                <button
                  onClick={handleNewCustomer}
                  disabled={!newCustomer.name || !newCustomer.phone}
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50 whitespace-nowrap"
                >
                  Add Customer
                </button>
                <button
                  onClick={() => setShowNewCustomerForm(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 whitespace-nowrap"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};